import type {
  HnsUri,
  HxnUri,
  HtsUri,
  ParsedHtsUri
} from '../types/uri.js';

const CONTROL_CHARS_REGEX =
  /[\u0000-\u001F\u007F-\u009F\s]/;

const AUTHORITY_REGEX =
  /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?$/i;

const RESOURCE_SEGMENT_REGEX =
  /^[A-Za-z0-9._~*-]+$/;

function requireString(
  value: unknown,
  name: string
): string {
  if (typeof value !== 'string') {
    throw new Error(`Invalid ${name} URI: must be a string`);
  }

  if (CONTROL_CHARS_REGEX.test(value)) {
    throw new Error(
      `Invalid ${name} URI: whitespace or control characters are not allowed`
    );
  }

  return value;
}

function rejectUnsupportedSuffix(
  value: string,
  name: string
): void {
  if (value.includes('?') || value.includes('#')) {
    throw new Error(
      `Invalid ${name} URI: query or fragment not supported`
    );
  }
}

export function parseHxnUri(value: unknown): HxnUri {
  const input = requireString(value, 'HXN');
  const lower = input.toLowerCase();

  let pathPart = '';
  if (lower.startsWith('hxn://')) {
    pathPart = input.slice('hxn://'.length);
  } else if (lower.startsWith('hns://')) {
    // Backward compatibility: seamlessly accept legacy hns:// and upgrade to canonical hxn://
    pathPart = input.slice('hns://'.length);
  } else {
    throw new Error(
      'Invalid HXN URI: missing scheme hxn:// or legacy hns://'
    );
  }

  if (!pathPart) {
    throw new Error('Invalid HXN URI: missing authority');
  }

  rejectUnsupportedSuffix(pathPart, 'HXN');

  if (pathPart.includes('//')) {
    throw new Error(
      'Invalid HXN URI: repeated separators are not allowed'
    );
  }

  const segments = pathPart.split('/');
  const authority = segments[0];

  if (!authority) {
    throw new Error('Invalid HXN URI: missing authority');
  }

  if (!AUTHORITY_REGEX.test(authority)) {
    throw new Error(
      'Invalid HXN URI: invalid authority'
    );
  }

  const pathSegments = segments.slice(1);

  if (
    pathSegments.length > 0 &&
    pathSegments[pathSegments.length - 1] === ''
  ) {
    pathSegments.pop();
  }

  for (const segment of pathSegments) {
    if (!RESOURCE_SEGMENT_REGEX.test(segment)) {
      throw new Error(
        'Invalid HXN URI: invalid path segment'
      );
    }
  }

  const canonicalAuthority = authority.toLowerCase();
  const canonical =
    pathSegments.length === 0
      ? `hxn://${canonicalAuthority}`
      : `hxn://${canonicalAuthority}/${pathSegments.join('/')}`;

  return canonical as HxnUri;
}

export function parseHnsUri(value: unknown): HnsUri {
  const input = requireString(value, 'HNS');
  const lower = input.toLowerCase();

  let pathPart = '';
  if (lower.startsWith('hns://')) {
    pathPart = input.slice('hns://'.length);
  } else if (lower.startsWith('hxn://')) {
    pathPart = input.slice('hxn://'.length);
  } else {
    throw new Error(
      'Invalid HNS URI: missing scheme hns://'
    );
  }

  if (!pathPart) {
    throw new Error('Invalid HNS URI: missing authority');
  }

  rejectUnsupportedSuffix(pathPart, 'HNS');

  if (pathPart.includes('//')) {
    throw new Error(
      'Invalid HNS URI: repeated separators are not allowed'
    );
  }

  const segments = pathPart.split('/');
  const authority = segments[0];

  if (!authority) {
    throw new Error('Invalid HNS URI: missing authority');
  }

  if (!AUTHORITY_REGEX.test(authority)) {
    throw new Error(
      'Invalid HNS URI: invalid authority'
    );
  }

  const pathSegments = segments.slice(1);

  if (
    pathSegments.length > 0 &&
    pathSegments[pathSegments.length - 1] === ''
  ) {
    pathSegments.pop();
  }

  for (const segment of pathSegments) {
    if (!RESOURCE_SEGMENT_REGEX.test(segment)) {
      throw new Error(
        'Invalid HNS URI: invalid path segment'
      );
    }
  }

  const canonicalAuthority = authority.toLowerCase();
  const canonical =
    pathSegments.length === 0
      ? `hns://${canonicalAuthority}`
      : `hns://${canonicalAuthority}/${pathSegments.join('/')}`;

  return canonical as HnsUri;
}

export function parseHtsUri(value: unknown): ParsedHtsUri {
  const input = requireString(value, 'HTS');

  if (!input.toLowerCase().startsWith('hts://')) {
    throw new Error(
      'Invalid HTS URI: missing scheme hts://'
    );
  }

  const pathPart = input.slice('hts://'.length);

  if (!pathPart) {
    throw new Error(
      'Invalid HTS URI: missing authority, plugin, and resource'
    );
  }

  rejectUnsupportedSuffix(pathPart, 'HTS');

  if (pathPart.includes('//')) {
    throw new Error(
      'Invalid HTS URI: repeated separators are not allowed'
    );
  }

  const segments = pathPart.split('/');

  if (
    segments.length > 0 &&
    segments[segments.length - 1] === ''
  ) {
    segments.pop();
  }

  const authority = segments[0];
  const plugin = segments[1];
  const resourceSegments = segments.slice(2);

  if (
    authority === undefined ||
    plugin === undefined ||
    resourceSegments.length === 0
  ) {
    throw new Error(
      'Invalid HTS URI: must contain authority, plugin, and resource'
    );
  }

  if (!AUTHORITY_REGEX.test(authority)) {
    throw new Error(
      'Invalid HTS URI: invalid authority'
    );
  }

  if (!/^[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i.test(plugin)) {
    throw new Error(
      'Invalid HTS URI: invalid plugin namespace'
    );
  }

  for (const segment of resourceSegments) {
    if (!RESOURCE_SEGMENT_REGEX.test(segment)) {
      throw new Error(
        'Invalid HTS URI: invalid resource segment'
      );
    }
  }

  const canonicalAuthority = authority.toLowerCase();
  const canonicalPlugin = plugin.toLowerCase();
  const resourceId = resourceSegments.join('/');

  const canonical =
    `hts://${canonicalAuthority}/${canonicalPlugin}/${resourceId}` as HtsUri;

  return {
    canonical,
    authority: canonicalAuthority,
    plugin: canonicalPlugin,
    resourceId
  };
}

export function isValidHxnUri(
  value: unknown
): value is HxnUri {
  try {
    parseHxnUri(value);
    return true;
  } catch {
    return false;
  }
}

export function isValidHnsUri(
  value: unknown
): value is HnsUri {
  try {
    parseHnsUri(value);
    return true;
  } catch {
    return false;
  }
}

export function isValidHtsUri(
  value: unknown
): value is HtsUri {
  try {
    parseHtsUri(value);
    return true;
  } catch {
    return false;
  }
}
